import { useEffect, useState } from "react";
import { api, type ThreadMessage } from "./api";

/**
 * One shared "unread" flag for the Messages nav item. The header polls (slowly) while the customer is elsewhere and
 * raises the flag when a reply arrives; the conversation page clears it whenever it shows the thread.
 * The poll asks the server not to mark anything read, so the flag follows the server exactly; opening the page marks it read.
 */

let unread = false;
const listeners = new Set<(value: boolean) => void>();

export const hasUnread = (messages: ThreadMessage[]) => messages.some((m) => m.direction === "out" && m.readAt === null);

function set(next: boolean) {
  if (next === unread) return;
  unread = next;
  for (const l of listeners) l(unread);
}

export function raiseUnread(messages: ThreadMessage[]) {
  if (hasUnread(messages)) set(true);
}

export function clearUnread() {
  set(false);
}

export function useUnread(poll: boolean, intervalMs = 30_000): boolean {
  const [value, setValue] = useState(unread);
  useEffect(() => {
    listeners.add(setValue);
    return () => {
      listeners.delete(setValue);
    };
  }, []);
  useEffect(() => {
    if (!poll) return;
    let alive = true;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      api
        .conversation({ markRead: false })
        .then((res) => {
          if (alive) {
            if (hasUnread(res.messages)) set(true);
            else set(false);
          }
        })
        .catch(() => undefined);
    };
    check();
    const id = window.setInterval(check, intervalMs);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [poll, intervalMs]);
  return value;
}
